import { useEffect, useState } from 'react'
import { AlertTriangle, ChevronRight, X } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { Input } from '@/core/ui/Input'
import { cn } from '@/lib/cn'
import {
  errosDaViagem,
  nowHHMM,
  viagemProducao,
  type CarreteiroFichaState,
  type ViagemDraft,
} from './carreteiroStore'
import { POSICOES_R, POSICOES_U, posicaoPermitida, type CampoViagem } from './carreteiroRegras'
import type {
  CatLocalCarga,
  CatLocalDescargaRod,
  CatLocalDescargaUrb,
} from '@/core/db/entities'
import {
  useLocaisCargaPorProjeto,
  useLocaisDescargaRod,
  useLocaisDescargaUrb,
} from '@/core/db/daos'
import { PickerSheet } from '@/features/common/PickerSheet'

/**
 * Formulário COMPLETO da viagem — editar viagem existente ou exceção vinda do
 * modal rápido ("Mais opções").
 *
 * Regras do SIGA:
 *   - Salvar só sem erros (mesma validação do modal rápido e do servidor).
 *   - Posição só aparece com destino pista (R) ou avenida/rua (U); trocar
 *     para um destino sem posição limpa o valor.
 *   - NF, largura e comprimento só existem em ficha com NF.
 *   - Qde VG não existe (SIGA grava 1) e NF é da ficha, não da viagem.
 *   - Apontador fica oculto (persistido = chapa do usuário).
 */

interface Props {
  ficha: CarreteiroFichaState
  initial: ViagemDraft
  /** Recusa do servidor apontando esta viagem — mostra a mensagem e destaca o campo. */
  destaque?: { campo: string | null; mensagem: string } | null
  onSave: (v: ViagemDraft) => void
  onDismiss: () => void
}

interface Local {
  id: number | null
  nome: string | null
}

export function ViagemFormSheet({ ficha, initial, destaque, onSave, onDismiss }: Props) {
  const servico = initial.servico
  const isEdit = ficha.viagens.some((v) => v.id === initial.id)
  const comNf = ficha.fichaConcreto === 'S'

  const [cargaHora, setCargaHora] = useState(initial.cargaHora ?? nowHHMM())
  const [cargaEstaca, setCargaEstaca] = useState(initial.cargaEstacaOrigem ?? '')
  const [cargaDtFixo, setCargaDtFixo] = useState(initial.cargaDtFixo?.toString() ?? '')
  const [origem, setOrigem] = useState<Local & { tipo: string | null }>({
    id: initial.localCargaId,
    nome: initial.localCargaNome,
    tipo: initial.localCargaOrigem,
  })
  const [destino, setDestino] = useState<Local & { deposito: string | null }>({
    id: initial.localDescargaId,
    nome: initial.localDescargaNome,
    deposito: initial.localDescargaDeposito,
  })
  const [picker, setPicker] = useState<'origem' | 'destino' | null>(null)

  const locaisCarga = useLocaisCargaPorProjeto(ficha.projetoId) ?? []
  const locaisDescargaRod = useLocaisDescargaRod(ficha.projetoId) ?? []
  const locaisDescargaUrb = useLocaisDescargaUrb(ficha.projetoId) ?? []
  const [descargaHora, setDescargaHora] = useState(initial.descargaHora ?? nowHHMM())
  const [descargaEstaca, setDescargaEstaca] = useState(initial.descargaEstacaDescarga ?? '')
  const [descargaBairro, setDescargaBairro] = useState(initial.descargaBairro ?? '')
  const [descargaRua, setDescargaRua] = useState(initial.descargaRua ?? '')
  const [largura, setLargura] = useState(initial.descargaLargura?.toString() ?? '')
  const [comprimento, setComprimento] = useState(initial.descargaComprimento?.toString() ?? '')
  const [posicao, setPosicao] = useState<string | null>(initial.descargaPosicao)
  const [nf, setNf] = useState(initial.nf ?? '')
  const [volume, setVolume] = useState(initial.volume?.toString() ?? '')

  const permitePosicao = posicaoPermitida(ficha.tipo, destino.deposito)
  const posicoes: readonly string[] = ficha.tipo === 'R' ? POSICOES_R : POSICOES_U
  const unidadeLabel = servico.unidadeNome ?? ''

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onDismiss])

  function trocarDestino(l: { id: number; nome: string; deposito: string | null }) {
    setDestino({ id: l.id, nome: l.nome, deposito: l.deposito })
    if (!posicaoPermitida(ficha.tipo, l.deposito)) setPosicao(null)
  }

  function build(): ViagemDraft {
    return {
      ...initial,
      servico,
      localCargaId: origem.id,
      localCargaNome: origem.nome,
      localCargaOrigem: origem.tipo,
      localDescargaId: destino.id,
      localDescargaNome: destino.nome,
      localDescargaDeposito: destino.deposito,
      cargaHora: cargaHora.trim() || null,
      cargaEstacaOrigem: ficha.tipo === 'R' ? cargaEstaca.trim() || null : null,
      // Apontadores NÃO são editados aqui — vêm do draft (= user.chapa).
      cargaChapaApontador: initial.cargaChapaApontador,
      cargaDtFixo: parseNum(cargaDtFixo),
      descargaHora: descargaHora.trim() || null,
      descargaEstacaDescarga: ficha.tipo === 'R' ? descargaEstaca.trim() || null : null,
      descargaBairro: ficha.tipo === 'U' ? descargaBairro.trim() || null : null,
      descargaRua: ficha.tipo === 'U' ? descargaRua.trim() || null : null,
      descargaChapaApontador: initial.descargaChapaApontador,
      descargaLargura: comNf ? parseNum(largura) : null,
      descargaComprimento: comNf ? parseNum(comprimento) : null,
      descargaPosicao: permitePosicao ? posicao : null,
      volume: parseNum(volume),
      nf: comNf ? nf.trim() || null : null,
    }
  }

  const atual = build()
  const erros = errosDaViagem(ficha, atual)
  const faltaNf = comNf && nf.trim() === ''
  const podeSalvar = erros.length === 0 && !faltaNf
  const erroDe = (campo: CampoViagem) => erros.find((e) => e.campo === campo)?.mensagem ?? null
  const destacado = (campo: CampoViagem) => destaque?.campo === campo
  const previewProducao = viagemProducao(ficha, atual)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        className="card-surface w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-12 rounded-full bg-on-surface-muted" />
        </div>

        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-border">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-xs font-bold text-accent uppercase">
              VIAGEM · {ficha.tipo === 'R' ? 'Rodoviário' : 'Urbana'}
              {ficha.mvePrefixo ? ` · ${ficha.mvePrefixo}` : ''}
              {comNf ? ' · com NF' : ''}
            </p>
            <h2 className="text-lg font-bold text-on-surface mt-1 truncate">{servico.nome}</h2>
            {servico.materialNome && (
              <p className="text-xs text-on-surface-dim mt-1">
                Material:{' '}
                <span className="text-on-surface font-medium">
                  {servico.materialNome}
                  {unidadeLabel ? ` (${unidadeLabel})` : ''}
                </span>
              </p>
            )}
            {erroDe('servico') && <p className="text-xs text-bad mt-1">{erroDe('servico')}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onDismiss} aria-label="Fechar">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {destaque && (
            <div className="rounded-lg border border-bad bg-bad/10 px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-bad shrink-0 mt-0.5" />
              <p className="text-xs text-on-surface">{destaque.mensagem}</p>
            </div>
          )}

          <Section title="CARGA">
            <PickerField
              label="Origem"
              value={origem.nome ?? 'Selecionar…'}
              onClick={() => setPicker('origem')}
              erro={erroDe('origem')}
              destacado={destacado('origem')}
            />
            <Pair>
              <LabeledInput
                label="Hora"
                value={cargaHora}
                onChange={setCargaHora}
                type="time"
                erro={erroDe('cargaHora')}
                destacado={destacado('cargaHora')}
              />
              <NumericField
                label="DT fixo (km)"
                value={cargaDtFixo}
                onChange={setCargaDtFixo}
                erro={erroDe('dtFixo')}
                destacado={destacado('dtFixo')}
              />
            </Pair>
            {ficha.tipo === 'R' && (
              <LabeledInput
                label="Estaca origem"
                value={cargaEstaca}
                onChange={setCargaEstaca}
                placeholder="ex.: 00001"
                maxLength={6}
                inputMode="numeric"
                erro={erroDe('estacaOrigem')}
                destacado={destacado('estacaOrigem')}
              />
            )}
          </Section>

          <Section title="DESCARGA">
            <PickerField
              label="Destino"
              value={destino.nome ?? 'Selecionar…'}
              onClick={() => setPicker('destino')}
              erro={erroDe('destino')}
              destacado={destacado('destino')}
            />
            {ficha.tipo === 'R' ? (
              <LabeledInput
                label="Estaca descarga"
                value={descargaEstaca}
                onChange={setDescargaEstaca}
                maxLength={6}
                inputMode="numeric"
                erro={erroDe('estacaDescarga')}
                destacado={destacado('estacaDescarga')}
              />
            ) : (
              <>
                <LabeledInput
                  label="Bairro (complemento)"
                  value={descargaBairro}
                  onChange={setDescargaBairro}
                  maxLength={50}
                />
                <LabeledInput
                  label="Rua (complemento)"
                  value={descargaRua}
                  onChange={setDescargaRua}
                  maxLength={50}
                />
              </>
            )}
            <LabeledInput
              label="Hora"
              value={descargaHora}
              onChange={setDescargaHora}
              type="time"
              erro={erroDe('descargaHora')}
              destacado={destacado('descargaHora')}
            />

            {permitePosicao && (
              <div>
                <label className="text-xs font-medium text-on-surface-dim uppercase">Posição</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {posicoes.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPosicao(posicao === p ? null : p)}
                      className={cn(
                        'rounded-full px-3 py-1.5 text-sm border transition-colors',
                        posicao === p
                          ? 'bg-accent text-bg-base border-accent'
                          : 'bg-surface-1 text-on-surface border-border hover:border-accent/60',
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {comNf && (
              <Pair>
                <NumericField label="Largura (m)" value={largura} onChange={setLargura} />
                <NumericField label="Comprimento (m)" value={comprimento} onChange={setComprimento} />
              </Pair>
            )}
          </Section>

          <Section title={comNf ? 'NF & QUANTIDADE' : 'QUANTIDADE'}>
            <div className={comNf ? 'grid grid-cols-2 gap-3' : ''}>
              {comNf && (
                <LabeledInput
                  label="NF / Ticket"
                  value={nf}
                  onChange={setNf}
                  maxLength={10}
                  erro={faltaNf ? 'Preencha a NF / ticket' : null}
                />
              )}
              <NumericField
                label={comNf ? 'Qtde NF (M³/Ton)' : 'Quantidade (M³/Ton)'}
                value={volume}
                onChange={setVolume}
                erro={erroDe('volume')}
                destacado={destacado('volume')}
              />
            </div>
            <div className="rounded-lg bg-surface-2/60 px-3 py-2 text-xs">
              <span className="text-on-surface-dim">Produção calculada:&nbsp;</span>
              <span className="text-on-surface font-bold">
                {previewProducao.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {unidadeLabel ? ` ${unidadeLabel}` : ''}
              </span>
              <p className="mt-1 text-on-surface-dim">
                volume × {ficha.tipo === 'R' ? 'DT calc (DT fixo + estacas)' : 'DT fixo'}, respeitando
                a distância mínima do contrato
              </p>
            </div>
          </Section>
        </div>

        <div className="border-t border-border px-4 py-3 space-y-2">
          {!podeSalvar && (
            <p className="text-xs text-warn text-center">
              {Array.from(
                new Set([...erros.map((e) => e.mensagem), ...(faltaNf ? ['Preencha a NF / ticket'] : [])]),
              ).join(' · ')}
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onDismiss} className="flex-1">
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => podeSalvar && onSave(atual)}
              disabled={!podeSalvar}
              className="flex-1"
            >
              {isEdit ? 'Atualizar viagem' : 'Salvar viagem'}
            </Button>
          </div>
        </div>
      </div>

      {picker === 'origem' && (
        <PickerSheet<CatLocalCarga>
          open
          title="Origem (local de carga)"
          items={locaisCarga}
          label={(l) => l.nome ?? l.complemento ?? '(sem identificação)'}
          onPick={(l) => {
            setOrigem({ id: l.id, nome: l.nome ?? l.complemento ?? '(sem identificação)', tipo: l.origem })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar origem"
          emptyMessage="Nenhuma origem cadastrada neste projeto"
        />
      )}
      {picker === 'destino' && ficha.tipo === 'U' && (
        <PickerSheet<CatLocalDescargaUrb>
          open
          title="Destino"
          items={locaisDescargaUrb}
          label={(l) => l.nome}
          sublabel={(l) => l.bairro ?? null}
          onPick={(l) => {
            trocarDestino(l)
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar destino"
          emptyMessage="Nenhum destino cadastrado"
        />
      )}
      {picker === 'destino' && ficha.tipo === 'R' && (
        <PickerSheet<CatLocalDescargaRod>
          open
          title="Destino"
          items={locaisDescargaRod}
          label={(l) => l.nome}
          sublabel={(l) => l.deposito ?? null}
          onPick={(l) => {
            trocarDestino(l)
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar destino"
          emptyMessage="Nenhum destino cadastrado"
        />
      )}
    </div>
  )
}

function PickerField({
  label,
  value,
  onClick,
  erro,
  destacado,
}: {
  label: string
  value: string
  onClick: () => void
  erro?: string | null
  destacado?: boolean
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full card-surface-variant text-left px-4 py-3 flex items-center gap-3 min-h-touch hover:bg-surface-2/80 transition-colors',
          (erro || destacado) && 'ring-1 ring-bad',
        )}
      >
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
          <p className="text-on-surface mt-0.5 truncate">{value}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-on-surface-dim" />
      </button>
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-bold text-accent uppercase">{title}</h3>
      {children}
    </section>
  )
}

function Pair({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  inputMode,
  erro,
  destacado,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  placeholder?: string
  type?: string
  maxLength?: number
  inputMode?: 'numeric' | 'text'
  erro?: string | null
  destacado?: boolean
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        className={cn('mt-1', (erro || destacado) && 'ring-1 ring-bad')}
      />
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}

function NumericField({
  label,
  value,
  onChange,
  erro,
  destacado,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  erro?: string | null
  destacado?: boolean
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => {
          const v = e.target.value
          if (v === '' || /^[0-9]*[.,]?[0-9]*$/.test(v)) onChange(v.replace(',', '.'))
        }}
        className={cn('mt-1', (erro || destacado) && 'ring-1 ring-bad')}
      />
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}

function parseNum(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
